import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import AppsContext, { ApplicationInterfaceTypeUnion } from './appContext';
import { isCatalogApp, translateMsol, tryTranslateError } from '@app/helpers/addEditApplication.helpers';
import { MODULE_NAME, SYNC_SOURCES_BACKER } from '@app/pages/ssoManager/addEditApplication.enums';
import { applicationsService, basicService } from '@app/services';
import { AxiosError, AxiosResponse } from 'axios';
import {
    fieldtype,
    ICredentialGraphApiVerificationParsedResults,
    ICredentialVerificationParsedResults,
    ICredentialVerificationResponse,
    ICustomAttribute,
    IDomainResult,
    ILocalLicense,
    ISyncSource,
    IUsageLocation,
    IVerifyGraphApiCredentialsBody,
    IVerifyMsolCredentialsBody
} from '@app/models';
import { AppMsolFederation } from './appMsol';
import { useDispatch, useSelector } from 'react-redux';
import { createTranslation, emptyGuid, federationTypeOptions, genericErrorToast, interpolateTranslation } from '@app/helpers';
import { addAppFinished, getLoaderBtn, handleGetProvisioningPoliciesDTOData } from '@app/store/actions';
import { useTypedTranslation } from '@app/hooks';
import { toast } from 'react-toastify';
import { AppGraphApiFederation } from '@app/components/SSOApps/addEditAppTabs/appMsol/appGraphApiFederation.component';
import { PageLoader, PasslySelect } from '@app/components';

interface IProps {
    state: any;
    setState: Function;
    setFederationError: Function;
    setIsDomainChanged: Function;
    isInvisible: boolean;
    editingApp: boolean;
    version?: number;
}

export interface IOffice365State {
    managedSetup: fieldtype & {
        managed: boolean;
        domain: string;
        domainGraphModule: string;
        active: boolean;
        domainResults: IDomainResult[];
        graphApiEnabled: boolean;
        graphApiTenantId: string;
        graphApiTenantIdGraphModule: string;
        graphApiClientId: string;
        graphApiClientIdGraphModule: string;
    };
    licenses: ILocalLicense[];
    selectedLicense: string;
    locations: IUsageLocation[];
    usageLocation: string;
    canConfigure: boolean;
    syncSources: ISyncSource[];
    selectedSyncSource: string;
    membershipSyncMode: string;
    syncGroupsEnabled: boolean;
    selectedGroupTypeSyncMode: string;
    removeGroupDuplicates: boolean;
    forceNewGroupCreation: boolean;
    importMsolEnabled: boolean;
    importMsolUsersEnabled: boolean;
    importMsolGroupsEnabled: boolean;
    importedUsersInitialGroupId: string;
    importedUsersProvisioningPolicyId: string;
    lastSyncTime: string | null;
    federationType: string;
    tenant?: string;
}

export const Federation: React.SFC<IProps> = ({
    state,
    setState,
    setFederationError,
    isInvisible,
    editingApp,
    version,
    setIsDomainChanged
}: IProps) => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();
    const [loadingData, setLoadingData] = useState(false);

    const appFinished = useSelector((state: any) => state.appLibrary.addAppFinished);

    const defaultLicense: ILocalLicense = {
        label: translation('MSOL_DO_NOT_LICENSE'),
        value: 'none'
    };

    const defaultLocation: IUsageLocation = {
        name: translation('MSOL_DO_NOT_LOCATION'),
        id: 'none'
    };

    const app = useContext(AppsContext);
    const [catalogApp, setCatalogApp] = useState(false);
    const [appLoadedForEdit, setAppLoadedForEdit] = useState(false);
    const [connected, setConnected] = useState('');
    const [localMsolModuleError, setLocalMsolModuleError] = useState(true);
    const [localGraphModuleError, setLocalGraphModuleError] = useState(true);
    const [localGraphApiError, setLocalGraphApiError] = useState(true);
    const [graphApiVerificationState, setGraphApiVerificationState] = useState<ICredentialGraphApiVerificationParsedResults | undefined>();
    const serviceConfiguration = JSON.parse(app.ServiceConfiguration);
    const [federationType, setFederationType] = useState(serviceConfiguration.template || MODULE_NAME.graphModule);
    const [savedDomainName, setSavedDomainName] = useState('');

    useEffect(() => {
        dispatch(handleGetProvisioningPoliciesDTOData());
    }, []);

    useEffect(() => {
        const msolModuleError =
            federationType === MODULE_NAME.msolModule &&
            (localMsolModuleError ||
                (state.managedSetup.graphApiEnabled && localGraphApiError) ||
                (state.managedSetup.graphApiEnabled && state.selectedLicense !== 'none' && state.usageLocation === 'none'));
        const graphModuleError = federationType === MODULE_NAME.graphModule && localGraphModuleError;

        if (msolModuleError || graphModuleError) {
            // update top level addeEditApp error when switching between managed and manual config
            setFederationError(true);
        } else {
            setFederationError(false);
        }
    }, [
        state.managedSetup.active,
        localMsolModuleError,
        localGraphModuleError,
        state.managedSetup.graphApiEnabled,
        localGraphApiError,
        state.selectedLicense,
        state.usageLocation,
        federationType
    ]);

    useEffect(() => {
        setState((prevState: any) => ({ ...prevState, federationType: federationType }));
    }, [federationType]);

    const onError = (error: AxiosError<any>) => {
        genericErrorToast(error);
    };

    useEffect(() => {
        if (appFinished.finished) {
            const isMsol = federationType === MODULE_NAME.msolModule;

            let paramsObj = {
                Managed: state.managedSetup.managed,
                Domain: state.managedSetup.domain,
                SyncGroupsEnabled: state.syncGroupsEnabled,
                GroupTypeSyncMode: state.selectedGroupTypeSyncMode,
                RemoveGroupDuplicates: state.removeGroupDuplicates,
                ForceNewGroupCreation: state.forceNewGroupCreation,
                SyncSource: state.selectedSyncSource,
                MembershipSyncMode: state.membershipSyncMode,
                DefaultLicense: state.selectedLicense,
                UsageLocation: state.usageLocation,
                importMsolEnabled: state.importMsolEnabled,
                importMsolUsersEnabled: state.importMsolUsersEnabled,
                importMsolGroupsEnabled: state.importMsolGroupsEnabled,
                ImportedUsersInitialGroupId: state.importedUsersInitialGroupId,
                ImportedUsersProvisioningPolicyId: state.importedUsersProvisioningPolicyId,
                LastSyncTime: state.lastSyncTime,
                AutoFederate: state.managedSetup.active
            };

            if (isMsol) {
                paramsObj = {
                    ...paramsObj,
                    ...{
                        UserName: state.managedSetup.username,
                        Password: state.managedSetup.password,
                        GraphApiEnabled: state.managedSetup.graphApiEnabled,
                        GraphApiTenantId: state.managedSetup.graphApiTenantId,
                        GraphApiClientId: state.managedSetup.graphApiClientId
                    }
                };
            } else {
                paramsObj = {
                    ...paramsObj,
                    ...{
                        Domain: state.managedSetup.domainGraphModule,
                        GraphApiTenantId: state.managedSetup.graphApiTenantIdGraphModule,
                        GraphApiClientId: state.managedSetup.graphApiClientIdGraphModule
                    }
                };
            }

            // handle office 365 custom action
            const customActionData = {
                request: {
                    Action: 'update',
                    Module: federationType,
                    Parameters: JSON.stringify(paramsObj)
                }
            };

            dispatch(addAppFinished(false, ''));

            if (appLoadedForEdit) {
                applicationsService.postUpdateOffice365Config(appFinished.appId, customActionData, onError, finishEdit);
            } else {
                finishAdd();
            }
        }
    }, [appFinished.finished]);

    const finishAdd = () => {
        dispatch(getLoaderBtn(false));
        basicService.redirect(`/sso/library/details/${appFinished.appId}`);
    };

    const finishEdit = (res?: AxiosResponse) => {
        dispatch(getLoaderBtn(false));

        if (res && !res.data.Success) {
            toast.error(res.data.Results);

            return;
        }

        basicService.redirect('/sso/library');
    };

    const updateManagedSetup = (name: string, value: unknown, isGraph?: boolean) => {
        if (name === 'domain' || name === 'domainGraphModule') {
            const isSavedNameDifferent = savedDomainName && savedDomainName !== value;

            setIsDomainChanged(isSavedNameDifferent);
        }

        if (isGraph) {
            if (name === 'domainGraphModule' || name === 'graphApiClientIdGraphModule' || name === 'graphApiTenantIdGraphModule') {
                setLocalGraphModuleError(true);
                resetGraphApiVerifiedState();
                resetDomainResults();
            }
        } else {
            if (name === 'domain' || name === 'username' || name === 'password') {
                setLocalMsolModuleError(true);
                resetDomainResults();
                resetGraphApiVerifiedState();
            }

            if (name === 'graphApiEnabled' || name === 'domain' || name === 'graphApiClientId' || name === 'graphApiTenantId') {
                setLocalGraphApiError(true);
                resetGraphApiVerifiedState();
            }
        }

        setState((prevState: any) => ({ ...prevState, managedSetup: { ...prevState.managedSetup, [name]: value } }));
    };

    const addDomainResult = (value: IDomainResult) => {
        setState((prevState: any) => ({ ...prevState, managedSetup: { ...prevState.managedSetup, domainResults: [value] } }));
    };

    const resetDomainResults = () => {
        let localConfig: IOffice365State = { ...state };

        localConfig.managedSetup.domainResults = [];

        setState(localConfig);
    };

    const setVerificationData = (module: string): IVerifyMsolCredentialsBody => {
        const isGraph = module === MODULE_NAME.graphModule;

        let params: any = {
            Domain: state.managedSetup.domain,
            UserName: state.managedSetup.username,
            Password: state.managedSetup.password
        };

        if (isGraph) {
            params = {
                TenantId: state.managedSetup.graphApiTenantIdGraphModule,
                ClientId: state.managedSetup.graphApiClientIdGraphModule,
                Domain: state.managedSetup.domainGraphModule
            };
        }

        return {
            request: {
                Action: 'VerifyFederation',
                Module: module,
                Parameters: JSON.stringify(params)
            }
        };
    };

    const setVerificationGraphApiData = (module: string): IVerifyGraphApiCredentialsBody => {
        return {
            request: {
                Action: 'VerifyGraphApi',
                Module: module,
                Parameters: JSON.stringify({
                    TenantId: state.managedSetup.graphApiTenantId,
                    ClientId: state.managedSetup.graphApiClientId,
                    Domain: state.managedSetup.domain
                })
            }
        };
    };

    const onVerifySuccess = (module: string) => (xhr: AxiosResponse<ICredentialVerificationResponse>) => {
        const isGraph = module === MODULE_NAME.graphModule;

        setLoadingData(false);

        if (xhr.data.Success) {
            credentialsVerified(JSON.parse(xhr.data.Results), 'PrincipalName');
        } else {
            credentialsFailed(xhr.data.Results);
        }

        if (isGraph) {
            setLocalGraphModuleError(!xhr.data.Success);
        } else {
            setLocalMsolModuleError(!xhr.data.Success);
        }
    };

    const onVerifyGraphApiSuccess = (xhr: AxiosResponse<ICredentialVerificationResponse>) => {
        const result = xhr.data.Success && xhr.data.Results ? JSON.parse(xhr.data.Results) : { isVerified: false, message: xhr.data.Results };

        setLoadingData(false);
        credentialsGraphApiVerified(result);

        if (result?.isVerified) {
            setLocalGraphApiError(false);
        } else {
            setLocalGraphApiError(true);
        }
    };

    const credentialsVerified = (results: ICredentialVerificationParsedResults, syncSource: string, existingState?: IOffice365State) => {
        // first update local config
        let localConfig: IOffice365State = existingState ? existingState : { ...state };

        localConfig.managedSetup.domainResults = [];
        localConfig.licenses = [defaultLicense];
        localConfig.syncSources = [];
        localConfig.tenant = results.tenant;

        const domainValid = results.domain.status === 'Verified',
            domainManaged = results.domain.authentication !== 'Federated',
            domainWarnings = [];

        let domainState = 'valid';

        if (domainManaged) {
            if (results.domain.name.indexOf('.onmicrosoft.com') > 0) {
                domainWarnings.push('MSOL_DOMAIN_IS_ONMICROSOFT');
                domainState = 'invalid';
            }
        } else if (results.federation === undefined || results.federation.issuerUri !== app.IdentityIssuerUri) {
            domainWarnings.push('MSOL_DOMAIN_NOT_MANAGED');
            domainState = 'warning';
        }

        localConfig.managedSetup.domainResults.push({
            message: interpolateTranslation(translation('MSOL_DOMAIN_IS'), [results.domain.authentication]),
            state: domainState,
            type: 'domain-auth',
            moreInfo: domainWarnings.length ? { expanded: domainState === 'invalid', messages: domainWarnings } : null
        });

        const issuersInfo: string[] = [];
        let issuerState;

        if (results.federation?.issuerUri) {
            if (results.federation.issuerUri !== app.IdentityIssuerUri) {
                issuerState = 'Invalid';
                issuersInfo.push(translation('ISSUER_DIFFERS'));
                issuersInfo.push(interpolateTranslation(translation('CURRENT_ISSUER'), [results.federation.issuerUri]));
                issuersInfo.push(interpolateTranslation(translation('PASSLY_ISSUER'), [app.IdentityIssuerUri]));
            } else {
                issuerState = 'Valid';
                issuersInfo.push(translation('ISSUER_MATCHES'));
                issuersInfo.push(interpolateTranslation(translation('CURRENT_ISSUER'), [results.federation.issuerUri]));
            }

            localConfig.managedSetup.domainResults.push({
                message: interpolateTranslation(translation('MSOL_ISSUER'), [issuerState]),
                type: 'issuer',
                state: issuerState === 'Valid' ? 'valid' : 'warning',
                moreInfo: { messages: issuersInfo }
            });
        }

        localConfig.managedSetup.domainResults.push({
            message: interpolateTranslation(translation('MSOL_SUPPORTS'), [results.domain.capabilities]),
            type: 'domain-capabilities',
            state: 'valid'
        });

        localConfig.managedSetup.domainResults.push({
            message: interpolateTranslation(translation('MSOL_DOMAIN_IS'), [results.domain.status]),
            type: 'domain-valid',
            state: domainValid ? 'valid' : 'invalid'
        });

        const licenses = results.licenses || [];
        const singular = interpolateTranslation(translation('MSOL_LICENSES_SINGULAR'), [licenses.length.toString()]),
            plural = interpolateTranslation(translation('MSOL_LICENSES_PLURAL'), [licenses.length.toString()]);

        const licenseChildren: string[] = [];

        licenses.forEach(function (l) {
            const msolLicensePart = translateMsol(l.skuPartNumber);
            const msolLicenseTranslation = createTranslation(msolLicensePart);
            const licenseName = msolLicenseTranslation === '' ? msolLicensePart : msolLicenseTranslation;

            licenseChildren.push(
                interpolateTranslation(translation('MSOL_LICENSES_USED'), [licenseName, l.consumedUnits.toString(), l.activeUnits.toString()])
            );

            localConfig.licenses.push({
                label: licenseName,
                value: l.skuPartNumber
            });
        });

        localConfig.managedSetup.domainResults.push({
            message: licenses?.length > 1 ? singular : plural,
            state: 'valid',
            type: 'domain-licenses',
            moreInfo: licenseChildren.length ? { messages: licenseChildren, icon: 'perm-identity' } : null
        });

        let canConfigureSync = true;
        localConfig.managedSetup.domainResults.forEach(function (r) {
            if (r.state === 'invalid') {
                canConfigureSync = false;
            }
        });

        localConfig.canConfigure = canConfigureSync;

        SYNC_SOURCES_BACKER.forEach((s: ISyncSource) => {
            const source: ISyncSource = {
                label: s.label,
                value: s.value,
                disabled: false
            };

            if (s.value === 'PrincipalNameWithSuffix') {
                source.label = `${s.label} ${results.orgSuffix ? '(' + results.orgSuffix + ')' : ''}`;
                source.disabled = !results.orgSuffix || results.orgHasMultipleApps;
            }

            localConfig.syncSources.push(source);

            if (source.value === syncSource) {
                localConfig.selectedSyncSource = source.value;
            }
        });

        if (results.locations) {
            localConfig.locations = [defaultLocation, ...results.locations];
        }

        if (existingState === undefined) {
            // this is a new config, allow state to be set here
            setState(localConfig);
        }

        // update app config
        if (domainManaged) {
            let attributeRules = JSON.parse(app.AttributeRules);

            attributeRules.Entries?.forEach(function (e: ICustomAttribute) {
                if (e.Type === 'http://schemas.xmlsoap.org/claims/UPN') {
                    if (state.selectedSyncSource === 'EmailAddress') {
                        e.Emit = '{User.EmailAddress}';
                    }

                    if (state.selectedSyncSource === 'PrincipalName') {
                        e.Emit = '{User.PrincipalName}';
                    }

                    if (state.selectedSyncSource === 'PrincipalNameWithSuffix') {
                        e.Emit = '{User.PrincipalName}{Organization.PrincipalNameSuffix}';
                    }

                    app.updateState('AttributeRules', JSON.stringify(attributeRules));
                }
            });
        }
    };

    const credentialsGraphApiVerified = (results: ICredentialGraphApiVerificationParsedResults) => {
        setGraphApiVerificationState(results);
    };

    const resetGraphApiVerifiedState = () => {
        setGraphApiVerificationState(undefined);
    };

    const credentialsFailed = (results: string) => {
        addDomainResult({
            message: translation(createTranslation(tryTranslateError(results))) || results,
            type: 'domain-failure',
            state: 'invalid'
        });
    };

    const onVerifyError = () => {
        setLoadingData(false);
    };

    const verifyCredentials = (module: string) => () => {
        setLoadingData(true);
        applicationsService.postVerifyOffice365Credentials(app.Id, setVerificationData(module), onVerifyError, onVerifySuccess(module));
    };

    const verifyGraphApiCredentials = (module: string) => () => {
        setLoadingData(true);
        applicationsService.postVerifyOffice365Credentials(app.Id, setVerificationGraphApiData(module), onVerifyError, onVerifyGraphApiSuccess);
    };

    const handleConfigResponse = (response: AxiosResponse<ICredentialVerificationResponse>) => {
        if (!response.data.Success) {
            credentialsFailed(response.data.Results);
            setLocalMsolModuleError(true);
            setLocalGraphModuleError(true);
            setLocalGraphApiError(true);
            setLoadingData(false);
            return;
        }

        if (response.data.Results === null) {
            setLoadingData(false);
            return;
        }

        let results = JSON.parse(response.data.Results);

        if (!results && !catalogApp) {
            updateManagedSetup('active', false);
            return;
        }

        const config = JSON.parse(app.ServiceConfiguration);
        const isMsolConfig = 'msol-configuration' in config;
        const isGraphConfig = 'graph-configuration' in config;

        if (isMsolConfig || isGraphConfig) {
            let existingLocalConfigState: IOffice365State = { ...state };
            const configuration = JSON.parse(isMsolConfig ? config['msol-configuration'] : config['graph-configuration']);

            setLocalMsolModuleError(false);
            setLocalGraphModuleError(false);
            setLocalGraphApiError(false);

            credentialsVerified(results, configuration.syncSource, existingLocalConfigState);
            setConnected(isMsolConfig ? 'msol-configuration' : 'graph-configuration');

            if (isMsolConfig) {
                existingLocalConfigState.managedSetup.domain = configuration.domain;
                existingLocalConfigState.managedSetup.username = configuration.userName;
                existingLocalConfigState.managedSetup.password = '';
                existingLocalConfigState.managedSetup.graphApiEnabled = configuration.graphApiEnabled;
                existingLocalConfigState.managedSetup.graphApiTenantId = configuration.graphApiTenantId;
                existingLocalConfigState.managedSetup.graphApiClientId = configuration.graphApiClientId;
            } else {
                existingLocalConfigState.managedSetup.domainGraphModule = configuration.domain;
                existingLocalConfigState.managedSetup.graphApiTenantIdGraphModule = configuration.graphApiTenantId;
                existingLocalConfigState.managedSetup.graphApiClientIdGraphModule = configuration.graphApiClientId;
            }

            existingLocalConfigState.managedSetup.managed = configuration.managed;
            existingLocalConfigState.syncGroupsEnabled = configuration.syncGroupsEnabled;
            existingLocalConfigState.selectedGroupTypeSyncMode =
                'groupTypeSyncMode' in configuration ? configuration.groupTypeSyncMode : 'IfTypeMatches';
            existingLocalConfigState.forceNewGroupCreation = configuration.forceNewGroupCreation;
            existingLocalConfigState.removeGroupDuplicates = configuration.removeGroupDuplicates;
            existingLocalConfigState.importMsolEnabled = configuration.importMsolEnabled;
            existingLocalConfigState.importMsolUsersEnabled = configuration.importMsolUsersEnabled;
            existingLocalConfigState.importMsolGroupsEnabled = configuration.importMsolGroupsEnabled;
            existingLocalConfigState.importedUsersInitialGroupId = configuration.importedUsersInitialGroupId;
            existingLocalConfigState.importedUsersProvisioningPolicyId = configuration.importedUsersProvisioningPolicyId;
            existingLocalConfigState.lastSyncTime = configuration.lastSyncTime;
            existingLocalConfigState.managedSetup.active = configuration.autoFederate;

            if (configuration.domain) {
                existingLocalConfigState.canConfigure = true;
                setSavedDomainName(configuration.domain);
            }

            existingLocalConfigState.licenses.forEach(function (l: ILocalLicense) {
                if (l.value === configuration.defaultLicense) {
                    existingLocalConfigState.selectedLicense = l.value;
                }
            });

            if (configuration.usageLocation) {
                existingLocalConfigState.usageLocation = configuration.usageLocation;
            }

            if (configuration.membershipSyncMode) {
                existingLocalConfigState.membershipSyncMode = configuration.membershipSyncMode;
            }

            setState(existingLocalConfigState);
        }

        setLoadingData(false);
    };

    const getOptions = () => {
        const office365Options = {
            request: {
                Module: federationType,
                Action: 'GetLatestFederationVerification',
                Parameters: null
            }
        };
        setLoadingData(true);
        applicationsService.postGetOffice365ConfigOptions(app.Id, office365Options, handleConfigResponse);
    };

    const handleGraphAPIConfigResponse = (response: AxiosResponse<ICredentialVerificationResponse>) => {
        if (!response.data.Success) {
            return;
        }

        onVerifyGraphApiSuccess(response);
    };

    const getGraphAPIVerificationMsol = () => {
        const office365Options = {
            request: {
                Module: MODULE_NAME.msolModule,
                Action: 'GetLatestGraphApiVerification',
                Parameters: null
            }
        };
        applicationsService.postGetOffice365ConfigOptions(app.Id, office365Options, handleGraphAPIConfigResponse);
    };

    const load = useCallback(
        (loadedApp: ApplicationInterfaceTypeUnion) => {
            if (isCatalogApp(loadedApp)) {
                setCatalogApp(true);
            }
        },
        [translation]
    );

    useEffect(() => {
        if (!appLoadedForEdit && app.Id && app.Id !== emptyGuid) {
            load(app);
            setAppLoadedForEdit(true);
        }
    }, [load, app]);

    useEffect(() => {
        if (!editingApp) {
            return;
        }

        const isMsol = state.federationType === MODULE_NAME.msolModule;
        const configuration = JSON.parse(app.ServiceConfiguration);
        const template = configuration.template;
        const fedConfig = isMsol ? 'msol-configuration' : 'graph-configuration';
        const parsedConfig = configuration[fedConfig] && JSON.parse(configuration[fedConfig]);

        setLocalMsolModuleError(true);
        setLocalGraphModuleError(true);
        setLocalGraphApiError(true);

        if (template === state.federationType && configuration[fedConfig]) {
            getOptions();
        }

        if (isMsol && configuration[fedConfig] && parsedConfig.graphApiEnabled) {
            getGraphAPIVerificationMsol();
        }
    }, [state.federationType]);

    const handleFederationTypeChange = (e: any) => {
        const { value } = e.target;

        setFederationType(value);
        resetDomainResults();
    };

    const handleOnDisconnect = (module: string) => () => {
        const disconnectRequestBody = {
            request: {
                Module: module,
                Action: 'disconnect',
                Parameters: null
            }
        };
        applicationsService.postDisconnectOffice365(app.Id, disconnectRequestBody, () => basicService.redirect(`/sso/library/details/${app.Id}`));
    };

    const msolModuleText = state.managedSetup.graphApiEnabled ? translation('MSOL_HELP_TEXT_WITH_GRAPH') : translation('MSOL_HELP_TEXT');

    return (
        <>
            <Grid container justifyContent='space-between' className={`configpanel-row ${isInvisible ? 'configpanel__invisible' : ''}`}>
                <Grid item xs={12} lg={8} className='configpanel-left'>
                    {version && version >= 2 ? (
                        <Box pt={4} />
                    ) : (
                        <Grid item xs={2}>
                            <Box pt={4} pb={2} pl={4}>
                                <PasslySelect
                                    name='federationType'
                                    value={federationType}
                                    onChange={handleFederationTypeChange}
                                    id='federationType'
                                    disabled={loadingData}
                                    options={federationTypeOptions}
                                />
                            </Box>
                        </Grid>
                    )}
                    <Box p={4} pt={0}>
                        {federationType === MODULE_NAME.msolModule ? (
                            <AppMsolFederation
                                state={state}
                                setState={setState}
                                handleOnDisconnect={handleOnDisconnect}
                                graphApiVerificationState={graphApiVerificationState}
                                updateManagedSetup={updateManagedSetup}
                                verifyCredentials={verifyCredentials}
                                verifyGraphApiCredentials={verifyGraphApiCredentials}
                                connected={connected === 'msol-configuration'}
                            />
                        ) : (
                            <AppGraphApiFederation
                                state={state}
                                setState={setState}
                                handleOnDisconnect={handleOnDisconnect}
                                updateManagedSetup={updateManagedSetup}
                                verifyCredentials={verifyCredentials}
                                connected={connected === 'graph-configuration'}
                            />
                        )}
                        {loadingData && <PageLoader />}
                    </Box>
                </Grid>
                <Grid item xs={12} lg={4} className='configpanel-right'>
                    <Box p={4}>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: federationType === MODULE_NAME.msolModule ? msolModuleText : translation('MSOL_HELP_TEXT_GRAPH')
                            }}
                        />
                        <div dangerouslySetInnerHTML={{ __html: translation('MSOL_HELP_TEXT_MANUAL') }} />
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};
